import { fmtDate } from "@/lib/dateFormat";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Gauge, Wrench, FileText, Receipt, Banknote, CalendarDays, Phone, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const CAT = {
  work: { icon: Wrench, cls: "bg-blue-100 text-blue-700 border-blue-200", tag: "Work" },
  quote: { icon: FileText, cls: "bg-amber-100 text-amber-700 border-amber-200", tag: "Quote / request" },
  financial: { icon: Receipt, cls: "bg-emerald-100 text-emerald-700 border-emerald-200", tag: "Financial" },
  call: { icon: Phone, cls: "bg-violet-100 text-violet-700 border-violet-200", tag: "Call" },
  enquiry: { icon: CalendarDays, cls: "bg-slate-100 text-slate-700 border-slate-200", tag: "Booking" },
};
const TYPE_ICON = { booking: CalendarDays, quotation: FileText, approval: FileText, check_in: ShieldCheck, job: Wrench, invoice: Receipt, payment: Banknote, call_log: Phone };
const fmt = (d) => fmtDate(d);

/** Vehicle service/repair history timeline + mileage + reminders. Reads /vehicles/{id}/timeline. */
export function VehicleTimeline({ vehicleId }) {
  const [data, setData] = useState(null);
  useEffect(() => { api(`/vehicles/${vehicleId}/timeline`).then(setData).catch(() => setData(null)); }, [vehicleId]);
  if (!data) return null;

  const { entries = [], mileage_history = [], recommendations = [], open_job, last_visit, next_service } = data;
  const latestMileage = mileage_history[0];

  return (
    <div className="mb-4 space-y-4" data-testid="vehicle-timeline">
      {/* Reminders / at-a-glance */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat icon={Clock} label="Last visit" value={fmt(last_visit)} testId="vt-last-visit" />
        <Stat icon={Gauge} label="Latest mileage" value={latestMileage ? `${latestMileage.mileage.toLocaleString()} mi` : "—"} testId="vt-mileage" />
        <Stat icon={Wrench} label="Open job" value={open_job ? (open_job.status || "open").replace(/_/g, " ") : "None"} link={open_job ? `/jobs/${open_job.id}` : null} testId="vt-open-job" />
        <Stat icon={CalendarDays} label="Next service" value={next_service?.date ? fmt(next_service.date) : next_service?.mileage ? `${Number(next_service.mileage).toLocaleString()} mi` : "Not set"} testId="vt-next-service" />
      </div>

      {recommendations.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="vt-recommendations">
          <p className="flex items-center gap-2 text-sm font-bold text-amber-900"><AlertTriangle className="h-4 w-4" />Outstanding recommendations ({recommendations.length})</p>
          <ul className="mt-2 space-y-1.5">
            {recommendations.map((r, i) => (
              <li key={i} className="flex items-center justify-between gap-3 text-sm text-amber-900">
                <span>{r.description} {r.urgency ? <em className="not-italic text-amber-700">· {r.urgency}</em> : null}</span>
                <Link to={`/jobs/${r.job_id}`} className="shrink-0 text-xs font-bold underline">from {r.job_title || "job"}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-3 font-display text-lg font-bold uppercase text-midnight">Service &amp; Repair History</h3>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No history recorded for this vehicle yet.</p>
        ) : (
          <ol className="relative space-y-3 border-l border-border pl-5">
            {entries.map((e) => {
              const cat = CAT[e.category] || CAT.work;
              const Icon = TYPE_ICON[e.type] || cat.icon;
              const body = (
                <div className="flex items-start gap-3">
                  <span className={`absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full border ${cat.cls}`}><Icon className="h-3 w-3" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-sm font-bold text-midnight">{e.title || cat.tag}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${cat.cls}`}>{cat.tag}</span>
                      <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{(e.status || "").replace(/_/g, " ")}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {fmt(e.date)}
                      {e.mileage ? ` · ${Number(e.mileage).toLocaleString()} mi` : ""}
                      {e.amount != null ? ` · £${Number(e.amount).toFixed(2)}` : ""}
                    </p>
                  </div>
                </div>
              );
              return (
                <li key={`${e.type}-${e.id}`} data-testid={`vt-entry-${e.type}`}>
                  {e.link ? <Link to={e.link} className="block rounded-lg py-1 hover:bg-secondary">{body}</Link> : <div className="py-1">{body}</div>}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, link, testId }) {
  const inner = (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={testId}>
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</p>
      <p className="mt-1 truncate text-sm font-bold capitalize text-midnight">{value}</p>
    </div>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
}
